import {
    formatIncomeSplitPercentagesNew,
    type IncomeSplitConfig,
    IncomeSplitDetails,
    type IncomeSplitRule,
    IncomeSplitRulesListNew,
    IncomeSplitRuleType,
} from '@fruitful/income-split';
import IncomeSplitEndOfRules from '@fruitful/income-split/src/components/IncomeSplitEndOfRules';
import {
    AnalyticsUserEvent,
    type DepositAccount,
    type ImAccount,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { useIsFocused } from '@react-navigation/native';
import { useFeatureGate } from '@statsig/expo-bindings';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Redirect, router } from 'expo-router';
import { useSetAtom } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import {
    ArrowSplit,
    BodyLargeEmphasized,
    BodyLargeRegular,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Checkbox,
    FootnoteRegular,
    H3Emphasized,
    Icon,
    InfoIcon,
    LabelRegular,
    ScreenContainer,
    Sheet,
    Spinner,
    SplitIcon,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { captureException, track } from '../../../logger/Logger';
import { SUPPORT_EMAIL } from '../../../settings/types/constants';
import { invalidateIncomeSplitAndMoneyMapQueries } from '../../hooks/income-split/invalidateIncomeSplitAndMoneyMapQueries';
import useSaveIncomeSplit from '../../hooks/income-split/useSaveIncomeSplitNew';
import useGetExistingPaycheckConfigs from '../../hooks/paycheck-routing/useGetPaycheckConfigs';
import useGetInternalAndExternalDepositAccounts from '../../hooks/useGetInternalAndExternalDepositAccounts';
import { incomeSplitAtom } from '../../state/PaycheckRoutingAtoms';
import type { BankAccountDisplayDetail } from '../../types/BankAccount';
import type { IncomeSplitContext } from '../../types/IncomeSplit';
import { PaycheckRoutingStatus } from '../../types/PaycheckRouting';
import { isAccountValidAsIncomeSplitSource } from '../../utils/IncomeSplitUtils';
import {
    FRUITFUL_FDIC_DISCLOSURE,
    INVESTMENT_PRODUCTS_FDIC_DISCLOSURE,
    ONE_TIME_EXTERNAL_TRANSFER_DISCLOSURE_TEXT,
    STOP_PAYMENT_DISCLOSURE_TEXT,
} from '../Disclosures';

interface HeaderProps {
    context: IncomeSplitContext;
    isMoneySystem: boolean;
}

const Header = ({ context, isMoneySystem }: HeaderProps) => {
    const manageText = isMoneySystem
        ? 'You can manage Income Split from your Money Map at any time.'
        : 'You can manage Income Split in your Fruitful Cash settings.';

    const headerMessage =
        context === 'goButtonFlow'
            ? `Review your income rules. ${manageText}`
            : 'Starting with your next deposit, transfers will process from top to bottom subject to available funds.';
    return <BodyMediumRegular>{headerMessage}</BodyMediumRegular>;
};

const InfoSheet = ({
    isOpen,
    onClose,
    executeOnlyOnPaychecks,
}: {
    isOpen: boolean;
    onClose: () => void;
    executeOnlyOnPaychecks: boolean;
}) => {
    return (
        <Sheet isOpen={isOpen} onClose={onClose} showCloseButton={false}>
            <YStack
                gap={32}
                paddingTop={16}
                maxWidth={Platform.OS === 'web' ? 700 : undefined}
            >
                <YStack gap={8}>
                    <BodyLargeEmphasized>
                        How rules are applied to{' '}
                        {executeOnlyOnPaychecks ? 'paychecks' : 'income'}
                    </BodyLargeEmphasized>
                    <BodyMediumRegular color="$gray1100">
                        These rules will be applied to any{' '}
                        {executeOnlyOnPaychecks ? 'paychecks' : 'income'}{' '}
                        arriving in the specified Fruitful bank accounts.
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$gray1100">
                        {executeOnlyOnPaychecks
                            ? 'Paychecks are defined as anything that looks like a direct deposit (we do our best to identify paychecks based on available transaction details)'
                            : 'Income is defined as any deposits made into these accounts, such as paychecks, interest earned, or cash back. Excludes ACH returns and reversals, provisional credits, and transfers initiated from Fruitful.'}
                    </BodyMediumRegular>
                </YStack>

                <Button
                    text="Dismiss"
                    variant={ButtonVariant.PRIMARY}
                    onPress={onClose}
                />
            </YStack>
        </Sheet>
    );
};

const InfoSheetIncome = ({
    isOpen,
    onClose,
    isJointMembership,
    isMoneySystem,
}: {
    isOpen: boolean;
    onClose: () => void;
    isJointMembership: boolean;
    isMoneySystem: boolean;
}) => {
    const manageText = isMoneySystem
        ? 'You can manage Income Split from your Money Map at any time.'
        : 'You can manage Income Split in your Fruitful Cash settings.';

    const infoMessage = isJointMembership
        ? `The following rules will be applied to all income arriving into any joint Fruitful bank account. ${manageText}`
        : `The following rules will be applied to all income arriving into any Fruitful bank account. ${manageText}`;
    return (
        <Sheet isOpen={isOpen} onClose={onClose} showCloseButton={true}>
            <YStack paddingHorizontal={16} maxWidth={500}>
                <View
                    alignSelf="center"
                    justifyContent="center"
                    alignItems="center"
                    width={80}
                    height={80}
                    padding={20}
                    marginVertical={16}
                    rotate="180deg"
                    borderRadius={16}
                    backgroundColor="$gray100"
                >
                    <Icon icon={SplitIcon} color="$black" size={32} />
                </View>

                <YStack alignItems="center" gap={8}>
                    <H3Emphasized>Income Split</H3Emphasized>
                </YStack>

                <YStack gap={16} marginTop={32}>
                    <YStack gap={8}>
                        <BodyLargeRegular>How it works</BodyLargeRegular>
                        <BodyMediumRegular color="$gray1000">
                            {infoMessage}
                        </BodyMediumRegular>
                    </YStack>

                    <YStack gap={8}>
                        <BodyLargeRegular>
                            What&apos;s classified as income?
                        </BodyLargeRegular>
                        <BodyMediumRegular color="$gray1000">
                            Income is defined as any deposits made into these
                            accounts, such as paychecks, interest earned, or
                            cash back. Excludes ACH returns and reversals,
                            provisional credits, and transfers initiated from
                            Fruitful.
                        </BodyMediumRegular>
                    </YStack>
                </YStack>

                <View marginTop={32} width="100%">
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Got it"
                        onPress={onClose}
                    />
                </View>
            </YStack>
        </Sheet>
    );
};

const Rules = ({
    incomeSplit,
    bankAccountDetails,
    externalAccounts,
    internalInvestmentAccounts,
    moneyMap,
    isMoneySystem = false,
}: {
    incomeSplit: IncomeSplitConfig;
    bankAccountDetails: BankAccountDisplayDetail[];
    externalAccounts: DepositAccount[];
    internalInvestmentAccounts: ImAccount[];
    moneyMap?: MoneyMapWithRulesAndAccounts;
    isMoneySystem?: boolean;
}) => {
    return (
        <IncomeSplitRulesListNew
            incomeSplit={incomeSplit}
            bankAccountDetails={bankAccountDetails}
            externalAccounts={externalAccounts}
            internalInvestmentAccounts={internalInvestmentAccounts}
            moneyMap={moneyMap}
            isMoneySystem={isMoneySystem}
        />
    );
};

export const TransferDisclosure = () => {
    return (
        <XStack
            backgroundColor="$gray200"
            borderRadius={16}
            alignItems="flex-start"
            padding={12}
            gap={8}
        >
            <View width={20} height={20}>
                <Icon icon={InfoIcon} size={20} />
            </View>
            <YStack flex={1} gap={8}>
                <FootnoteRegular>
                    {ONE_TIME_EXTERNAL_TRANSFER_DISCLOSURE_TEXT}
                </FootnoteRegular>
                <FootnoteRegular>
                    {'\n'}
                    {STOP_PAYMENT_DISCLOSURE_TEXT}
                </FootnoteRegular>
            </YStack>
        </XStack>
    );
};

export const IncomeSplitAgreement = ({
    id,
    checked,
    setChecked,
}: {
    id: string;
    checked: boolean;
    setChecked: (checked: boolean) => void;
}) => {
    let labelContent: React.ReactNode = <></>;

    if (id === 'combined-accept') {
        labelContent = (
            <>
                I acknowledge I have read and agreed to the{' '}
                <Link
                    href="https://www.fruitful.com/legal/income-split-agreement"
                    target="_blank"
                    onPress={(e) => {
                        e.stopPropagation(); // prevents triggering the parent's onPress
                    }}
                >
                    <BodySmallRegular textDecorationLine="underline">
                        Income Split Terms and Conditions
                    </BodySmallRegular>
                </Link>{' '}
                and authorize Fruitful to initiate the transfers of my money as
                indicated above.
            </>
        );
    }

    return (
        <XStack
            gap={8}
            onPress={() => {
                setChecked(!checked);
            }}
            cursor="pointer"
        >
            <Checkbox
                id={id}
                checked={checked}
                onCheckedChange={(isChecked) => {
                    setChecked(isChecked as boolean);
                }}
            />
            <FootnoteRegular flexShrink={1}>{labelContent}</FootnoteRegular>
        </XStack>
    );
};

export const IncomeSplitReviewNew = ({
    incomeSplit,
    bankAccountDetails,
    internalInvestmentAccounts = [],
    context,
    incomeSplitComplete,
    onSubmit,
    onIncomeSplitCreated,
    moneyMap,
}: {
    incomeSplit: IncomeSplitConfig | null;
    bankAccountDetails: BankAccountDisplayDetail[];
    internalInvestmentAccounts?: ImAccount[];
    context: IncomeSplitContext;
    incomeSplitComplete?: boolean;
    onSubmit?: () => void;
    onIncomeSplitCreated?: () => void;
    moneyMap?: MoneyMapWithRulesAndAccounts;
}) => {
    const { value: enableMoneySystemTab } = useFeatureGate(
        FeatureFlags.ENABLE_MONEY_SYSTEM_TAB,
    );
    const setIncomeSplit = useSetAtom(incomeSplitAtom);

    const [isSavingConfig, setIsSavingConfig] = useState(false);
    const [hasAcceptedAgreement, setHasAcceptedAgreement] = useState(false);
    const isFocused = useIsFocused();
    const didAutoOpenInfoSheet = useRef(false);
    const [infoSheetOpen, setInfoSheetOpen] = useState(false);

    // Open once, on the focused review. Saving refetches the money map and
    // remounts this screen under confirmation; the sheet portals to the app
    // root, so that remount would cover the confirmation screen.
    useEffect(() => {
        if (
            !isFocused ||
            didAutoOpenInfoSheet.current ||
            incomeSplitComplete !== false ||
            context !== 'goButtonFlow'
        ) {
            return;
        }
        didAutoOpenInfoSheet.current = true;
        setInfoSheetOpen(true);
    }, [isFocused, incomeSplitComplete, context]);

    const { mutate: createIncomeSplit } = useSaveIncomeSplit();
    const queryClient = useQueryClient();
    const toast = useToastController();

    const { data: depositAccounts } = useGetInternalAndExternalDepositAccounts(
        {},
    );

    const externalAccounts = (depositAccounts ?? []).filter(
        (d) => d.isExternal,
    );

    const { paycheckRoutingConfigs: incomeSplitConfigs } =
        useGetExistingPaycheckConfigs();

    const incomeSplitAccounts = new Set(
        incomeSplit?.accounts.map((a) => a.accountId),
    );

    const incomeSplitExists = incomeSplitConfigs?.some((a) =>
        incomeSplitAccounts.has(a.accountId),
    );

    const user = useAuthedUser();
    const isJointMembership = !!user.partnerMember;
    const totalEligibleAccounts = bankAccountDetails.filter((account) =>
        isAccountValidAsIncomeSplitSource(user, account),
    );

    if (!incomeSplit) {
        captureException(
            new Error(
                'Tried to load IncomeSplitReview without an IncomeSplitConfig',
            ),
        );
        toast.show(
            `There was an error while loading your Income Split. Please reach out to support at ${SUPPORT_EMAIL}`,
        );
        return context === 'goButtonFlow' ? (
            <Redirect href="/home/roadmap" />
        ) : (
            <Redirect href="/home/banking" />
        );
    }

    const saveIncomeSplit = () => {
        setIsSavingConfig(true);
        onSubmit?.();

        const {
            id: incomeSplitId,
            isNew,
            membershipId,
            createdAt,
            updatedAt,
            ...rest
        } = incomeSplit;

        const ruleSets = (incomeSplit.ruleSets ?? []).map((group) => ({
            incomeSplitConfigId: incomeSplitId as string,
            name: group.name,
            priority: group.priority,
            rules: group.rules.map((rule) => {
                const { accountMetadata, ...rest } = rule;

                const { type, percentage } = rest;

                const computedPercentage =
                    type === IncomeSplitRuleType.PERCENTAGE
                        ? (percentage ?? 100) / 100
                        : percentage != null
                          ? percentage / 100
                          : undefined;

                return {
                    ...rest,
                    percentage: computedPercentage,
                    type,
                } satisfies IncomeSplitRule;
            }),
        }));

        const newIncomeSplit: IncomeSplitConfig = {
            ...rest,
            status: PaycheckRoutingStatus.ACTIVE,
            ruleSets,
            ...(incomeSplitId != null ? { id: incomeSplitId } : {}),
        };

        createIncomeSplit(
            { body: newIncomeSplit },
            {
                onSuccess: (result) => {
                    setIsSavingConfig(false);
                    setIncomeSplit(formatIncomeSplitPercentagesNew(result));
                    void invalidateIncomeSplitAndMoneyMapQueries(queryClient);
                    onIncomeSplitCreated?.();
                    router.navigate(
                        `/income-split/confirmation?isEdit=${incomeSplitExists}&context=${context}`,
                    );
                },
                onError: (error) => {
                    setIsSavingConfig(false);
                    toast.show('Error saving Income Split rules');
                    captureException(error);
                },
            },
        );
    };

    return (
        <>
            <ScreenContainer
                title={
                    context === 'incomeSplit'
                        ? 'Review Income Split'
                        : undefined
                }
                hasBackButton={context === 'incomeSplit'}
                onBack={
                    context === 'incomeSplit'
                        ? () => {
                              router.back();
                          }
                        : undefined
                }
                // In the Go flow this is nested under GoWorkflowHeader, whose
                // SafeAreaView already reserves the top inset — without this,
                // ScreenContainer reserves it again since it renders no header
                // of its own here (title/hasBackButton unset), doubling the
                // gap above the content.
                useSafeAreaInsetTop={context !== 'goButtonFlow'}
            >
                <YStack gap={24} justifyContent="space-between">
                    <YStack gap={16}>
                        <Header
                            context={context}
                            isMoneySystem={enableMoneySystemTab}
                        />
                        <YStack gap={8} marginBottom={52}>
                            <IncomeSplitDetails
                                incomeSplit={incomeSplit}
                                totalEligibleAccounts={totalEligibleAccounts}
                                icon={InfoIcon}
                                onIconPress={() => {
                                    setInfoSheetOpen(true);
                                }}
                            />
                            <YStack alignItems="center" paddingVertical={8}>
                                <ArrowSplit />
                            </YStack>
                            <Rules
                                incomeSplit={incomeSplit}
                                externalAccounts={externalAccounts}
                                bankAccountDetails={bankAccountDetails}
                                internalInvestmentAccounts={
                                    internalInvestmentAccounts
                                }
                                moneyMap={moneyMap}
                                isMoneySystem={enableMoneySystemTab}
                            />
                            <IncomeSplitEndOfRules />
                        </YStack>
                        <TransferDisclosure />
                        <IncomeSplitAgreement
                            id="combined-accept"
                            checked={hasAcceptedAgreement}
                            setChecked={(checked) => {
                                setHasAcceptedAgreement(checked);
                            }}
                        />
                    </YStack>

                    <YStack>
                        <Button
                            text={
                                isSavingConfig
                                    ? ''
                                    : 'Confirm and turn on Income Split'
                            }
                            icon={isSavingConfig ? <Spinner /> : null}
                            variant={ButtonVariant.PRIMARY}
                            onPress={saveIncomeSplit}
                            disabled={!hasAcceptedAgreement}
                        />
                        <LabelRegular color="$gray900" marginTop={24}>
                            {FRUITFUL_FDIC_DISCLOSURE}
                            {'\n\n'}
                            {INVESTMENT_PRODUCTS_FDIC_DISCLOSURE}
                        </LabelRegular>
                    </YStack>
                </YStack>
            </ScreenContainer>
            {isFocused &&
                (incomeSplit.onlyExecuteOnPaychecks ? (
                    <InfoSheet
                        isOpen={infoSheetOpen}
                        onClose={() => {
                            track(
                                AnalyticsUserEvent.INCOME_SPLIT_INFO_SHEET_DISMISSED,
                            );
                            setInfoSheetOpen(false);
                        }}
                        executeOnlyOnPaychecks={
                            incomeSplit.onlyExecuteOnPaychecks
                        }
                    />
                ) : (
                    <InfoSheetIncome
                        isOpen={infoSheetOpen}
                        onClose={() => {
                            track(
                                AnalyticsUserEvent.INCOME_SPLIT_INFO_SHEET_DISMISSED,
                            );
                            setInfoSheetOpen(false);
                        }}
                        isJointMembership={isJointMembership}
                        isMoneySystem={enableMoneySystemTab}
                    />
                ))}
        </>
    );
};
